import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";

/** Auth state:
 *   session = null    → checking (spinner)
 *   session = false   → not authenticated
 *   session = {...}   → authenticated (user, tenant, role, permissions, features)
 */
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [pendingActionsCount, setPendingActionsCount] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setSession(data);
      return data;
    } catch {
      setSession(false);
      return null;
    }
  }, []);

  const refreshPendingCount = useCallback(async () => {
    try {
      const { data } = await api.get("/tenant/pending-actions");
      setPendingActionsCount(data.pending_count ?? data.count ?? 0);
    } catch {
      setPendingActionsCount(0);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Poll pending actions every 60s while a session is active.
  useEffect(() => {
    if (!session || !session.permissions?.includes("tenant.read")) return;
    refreshPendingCount();
    const id = setInterval(refreshPendingCount, 60000);
    return () => clearInterval(id);
  }, [session, refreshPendingCount]);

  const login = async (email, password) => {
    try {
      const r = await api.post("/auth/login", { email, password });
      if (r.data?.requires_2fa) {
        return { ok: false, requires_2fa: true,
                 challenge_token: r.data.challenge_token,
                 email: r.data.email };
      }
      // Refresh peut échouer si le token retourné a un scope restreint
      // (ex : `enroll_2fa_only` quand le grace period 2FA est expiré).
      // On ne considère PAS l'échec comme une erreur de connexion — le
      // caller (Login.jsx) redirige vers /app/settings/security via
      // res.session_data.scope. Sans ce try/catch, `apiError` stringify
      // le detail Object → « [object Object] » côté UI.
      let s = null;
      try { s = await refresh(); } catch { /* refresh peut être bloqué par un scope restreint */ }
      try {
        const { identifyUser, track } = await import("@/telemetry");
        if (s?.user?.id) {
          identifyUser({ userId: s.user.id, tenantId: s.tenant?.id, role: s.role });
          track("login", {}, { tenantId: s.tenant?.id, role: s.role });
        }
      } catch { /* telemetry never blocks */ }
      return { ok: true, session_data: r.data };
    } catch (e) {
      return { ok: false, error: apiError(e, "Connexion impossible.") };
    }
  };

  const verify2FA = async (challenge_token, code) => {
    try {
      await api.post("/auth/2fa/verify", { challenge_token, code });
      await refresh();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: apiError(e, "Code 2FA invalide.") };
    }
  };

  const register = async (payload) => {
    try {
      await api.post("/auth/register", payload);
      await refresh();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: apiError(e, "Inscription impossible.") };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    setSession(false);
  };

  const switchTenant = async (tenantId) => {
    try {
      await api.post("/me/switch-tenant", { tenant_id: tenantId });
      await refresh();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: apiError(e) };
    }
  };

  const hasPermission = (perm) => Boolean(session?.permissions?.includes(perm));
  const hasFeature = (feat) => Boolean(session?.features?.includes(feat));

  return (
    <AuthContext.Provider
      value={{ session, refresh, login, verify2FA, register, logout, switchTenant, hasPermission, hasFeature,
               pendingActionsCount, refreshPendingCount }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
